import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

export type Lang = "es" | "en" | "pt";
export const LANGS: { code: Lang; label: string; full: string }[] = [
  { code: "es", label: "ES", full: "Español" },
  { code: "en", label: "EN", full: "English" },
  { code: "pt", label: "PT", full: "Português" },
];

const STORAGE_KEY = "site-lang";
const EVENT = "site-lang-change";

type Dict = Record<string, string>;
type Translations = Record<Lang, Dict>;

// Static UI copy. Extend per feature as we translate more surfaces.
export const translations: Translations = {
  es: {
    "nav.home": "Home",
    "nav.about": "Quiénes Somos",
    "nav.solutions": "Soluciones",
    "nav.clients": "Nuestros Clientes",
    "nav.communications": "Comunicaciones",
    "nav.contact": "Contacto",
    "nav.access": "ACCEDER",
    "nav.solutions.heading": "Soluciones Ecosistema MineClass",
    "nav.about.what": "Qué es Mine Class",
    "nav.about.team": "Nuestro Equipo",
    "nav.about.alliances": "Alianzas y Certificaciones",
    "nav.solutions.accreditation": "Acreditación Contratistas",
    "nav.solutions.training": "Capacitación Empresas",
    "nav.solutions.tech": "Tecnología de Aprendizaje",
    "nav.solutions.courses": "Cursos Personas",
    "nav.solutions.programs": "Cursos para Empresas Mineras",
    "nav.solutions.convenio": "Convenio U. Mayor",
    "nav.solutions.certification": "Certificación Mine Class",
    "nav.solutions.sence": "Cursos SENCE",
    "nav.clients.trust": "Han confiado en nosotros",
    "nav.clients.cases": "Casos de éxito",
    "nav.comms.newsletter": "Newsletter",
    "nav.comms.news": "Noticias",
    "nav.comms.media": "Multimedia",
    "nav.comms.podcast": "Podcast",
    "nav.comms.docs": "Documentos",
    "nav.platform.otec": "Plataforma OTEC",
    "nav.platform.codelco": "Plataforma Codelco",
    "nav.platform.lms": "Plataforma LMS",
  },
  en: {
    "nav.home": "Home",
    "nav.about": "About Us",
    "nav.solutions": "Solutions",
    "nav.clients": "Our Clients",
    "nav.communications": "News",
    "nav.contact": "Contact",
    "nav.access": "SIGN IN",
    "nav.solutions.heading": "MineClass Ecosystem Solutions",
    "nav.about.what": "What is Mine Class",
    "nav.about.team": "Our Team",
    "nav.about.alliances": "Partnerships & Certifications",
    "nav.solutions.accreditation": "Contractor Accreditation",
    "nav.solutions.training": "Corporate Training",
    "nav.solutions.tech": "Learning Technology",
    "nav.solutions.courses": "Courses for Individuals",
    "nav.solutions.programs": "Courses for Mining Companies",
    "nav.solutions.convenio": "U. Mayor Agreement",
    "nav.solutions.certification": "Mine Class Certification",
    "nav.solutions.sence": "SENCE Courses",
    "nav.clients.trust": "Who trusts us",
    "nav.clients.cases": "Success Stories",
    "nav.comms.newsletter": "Newsletter",
    "nav.comms.news": "News",
    "nav.comms.media": "Multimedia",
    "nav.comms.podcast": "Podcast",
    "nav.comms.docs": "Documents",
    "nav.platform.otec": "OTEC Platform",
    "nav.platform.codelco": "Codelco Platform",
    "nav.platform.lms": "LMS Platform",
  },
  pt: {
    "nav.home": "Início",
    "nav.about": "Sobre Nós",
    "nav.solutions": "Soluções",
    "nav.clients": "Nossos Clientes",
    "nav.communications": "Comunicações",
    "nav.contact": "Contato",
    "nav.access": "ACESSAR",
    "nav.solutions.heading": "Soluções do Ecossistema MineClass",
    "nav.about.what": "O que é Mine Class",
    "nav.about.team": "Nossa Equipe",
    "nav.about.alliances": "Alianças e Certificações",
    "nav.solutions.accreditation": "Credenciamento de Contratados",
    "nav.solutions.training": "Capacitação Empresarial",
    "nav.solutions.tech": "Tecnologia de Aprendizagem",
    "nav.solutions.courses": "Cursos para Pessoas",
    "nav.solutions.programs": "Cursos para Empresas de Mineração",
    "nav.solutions.convenio": "Convênio U. Mayor",
    "nav.solutions.certification": "Certificação Mine Class",
    "nav.solutions.sence": "Cursos SENCE",
    "nav.clients.trust": "Quem confia em nós",
    "nav.clients.cases": "Casos de sucesso",
    "nav.comms.newsletter": "Newsletter",
    "nav.comms.news": "Notícias",
    "nav.comms.media": "Multimídia",
    "nav.comms.podcast": "Podcast",
    "nav.comms.docs": "Documentos",
    "nav.platform.otec": "Plataforma OTEC",
    "nav.platform.codelco": "Plataforma Codelco",
    "nav.platform.lms": "Plataforma LMS",
  },
};

type Ctx = { lang: Lang; setLang: (l: Lang) => void; t: (key: string) => string };
const I18nContext = createContext<Ctx | null>(null);

function readInitial(): Lang {
  if (typeof window === "undefined") return "es";
  const stored = window.localStorage.getItem(STORAGE_KEY) as Lang | null;
  if (stored && LANGS.some((l) => l.code === stored)) return stored;
  return "es";
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>("es");

  useEffect(() => {
    setLangState(readInitial());
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<Lang>).detail;
      if (detail && LANGS.some((l) => l.code === detail)) setLangState(detail);
    };
    window.addEventListener(EVENT, handler);
    return () => window.removeEventListener(EVENT, handler);
  }, []);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, l);
      window.dispatchEvent(new CustomEvent(EVENT, { detail: l }));
    }
  }, []);

  const t = useCallback(
    (key: string) => translations[lang]?.[key] ?? translations.es[key] ?? key,
    [lang],
  );

  return <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>;
}

export function useI18n(): Ctx {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Fallback so components rendered outside the provider (or during SSR) still work.
    return {
      lang: "es",
      setLang: () => {},
      t: (key: string) => translations.es[key] ?? key,
    };
  }
  return ctx;
}

/** Pick a translated field from a DB row, falling back to Spanish. */
export function pickLang<T extends Record<string, unknown>>(
  row: T,
  base: string,
  lang: Lang,
): string {
  const suffix = lang === "es" ? "" : `_${lang}`;
  const val = row[`${base}${suffix}`] ?? row[base];
  return typeof val === "string" ? val : "";
}
